import { useEffect, useRef, useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Search } from "lucide-react";

declare global {
  interface Window {
    google?: any;
    __initSchoolsMap?: () => void;
  }
}

export type SchoolPin = {
  id: string;
  name: string;
  district: string | null;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
  student_count: number;
  staff_count?: number;
  subscription_plan: string;
  health_score: number;
  active: boolean;
  registration_no?: string | null;
  phone?: string | null;
  email?: string | null;
  school_type?: string | null;
};

import { loadGoogleMaps as loadMapsApi } from "@/lib/google-maps-loader";

function planColor(plan: string) {
  return plan === "enterprise" ? "#7B1416" : plan === "standard" ? "#A42022" : "#C4A882";
}

export function SchoolsMap({ schools, onSelect, height = 480 }: { schools: SchoolPin[]; onSelect?: (s: SchoolPin) => void; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [district, setDistrict] = useState<string>("all");
  const [plan, setPlan] = useState<string>("all");
  const [health, setHealth] = useState<[number, number]>([0, 100]);
  const [selected, setSelected] = useState<SchoolPin | null>(null);

  const districts = useMemo(() => Array.from(new Set(schools.map(s => s.district).filter(Boolean))).sort() as string[], [schools]);

  const filtered = useMemo(() => schools.filter(s => {
    if (q && !(`${s.name} ${s.registration_no ?? ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
    if (district !== "all" && s.district !== district) return false;
    if (plan !== "all" && s.subscription_plan !== plan) return false;
    if (s.health_score < health[0] || s.health_score > health[1]) return false;
    return true;
  }), [schools, q, district, plan, health]);

  useEffect(() => {
    let cancelled = false;
    loadMapsApi()
      .then(() => {
        if (cancelled || !ref.current || !window.google) return;
        mapRef.current = new window.google.maps.Map(ref.current, {
          center: { lat: 1.3733, lng: 32.2903 },
          zoom: 7,
          mapTypeControl: false,
          streetViewControl: false,
          styles: [
            { elementType: "geometry", stylers: [{ color: "#f5efe3" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#f5efe3" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#3b3a2f" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#aac8c1" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#e8dfcb" }] },
            { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c4b69a" }] },
            { featureType: "poi", stylers: [{ visibility: "off" }] },
          ],
        });
      })
      .catch((e) => setErr(e.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    for (const s of filtered) {
      if (s.latitude == null || s.longitude == null) continue;
      const color = planColor(s.subscription_plan);
      const marker = new window.google.maps.Marker({
        position: { lat: s.latitude, lng: s.longitude },
        map: mapRef.current,
        title: s.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: Math.max(8, Math.min(18, Math.sqrt(s.student_count) / 4)),
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
      });
      marker.addListener("click", () => {
        setSelected(s);
        onSelect?.(s);
      });
      markersRef.current.push(marker);
    }
  }, [filtered, onSelect]);

  if (err) {
    return <div className="grid h-[420px] place-items-center rounded-xl border border-dashed bg-muted/30 text-sm text-muted-foreground">Maps unavailable: {err}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search school name or registration no…" className="pl-8" />
        </div>
        <Select value={district} onValueChange={setDistrict}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All districts</SelectItem>
            {districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex w-[220px] items-center gap-2 rounded-md border px-3 py-1.5 text-xs">
          <span className="text-muted-foreground">Health</span>
          <Slider value={health} onValueChange={(v) => setHealth([v[0], v[1]] as [number, number])} min={0} max={100} step={5} className="flex-1" />
          <span className="font-mono">{health[0]}–{health[1]}</span>
        </div>
        <Badge variant="secondary">{filtered.length}/{schools.length}</Badge>
      </div>

      <div className="relative">
        <div ref={ref} style={{ height, width: "100%", borderRadius: 12 }} />
        {selected && (
          <div className="absolute right-4 top-4 z-10 w-[340px] rounded-2xl border bg-card/95 p-4 shadow-2xl backdrop-blur">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg leading-tight">{selected.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{selected.district ?? "—"} · {selected.region ?? "—"}</div>
              </div>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="font-display text-lg">{selected.student_count.toLocaleString()}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Students</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="font-display text-lg">{selected.staff_count ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Staff</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="font-display text-lg" style={{ color: selected.health_score >= 85 ? "#7B1416" : selected.health_score >= 70 ? "#C4A882" : "#A42022" }}>{selected.health_score}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Health</div>
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><Badge style={{ backgroundColor: planColor(selected.subscription_plan), color: "#fff" }}>{selected.subscription_plan}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span>{selected.school_type ?? "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reg no</span><span className="font-mono">{selected.registration_no ?? "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className={selected.active ? "text-success" : "text-destructive"}>{selected.active ? "Active" : "Suspended"}</span></div>
            </div>
            <div className="mt-3 flex gap-2">
              <button className="flex-1 rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:opacity-90" onClick={() => onSelect?.(selected)}>Open profile →</button>
              {selected.latitude && (
                <a className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted" target="_blank" rel="noreferrer"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${selected.latitude},${selected.longitude}`}>Directions</a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
